import React from 'react';
import styled from 'styled-components';

import { ColorVariantEnum, FinePrintEnum, MarginSizeEnum, Text, WeightEnum } from '../../atoms/text';
import { TooltipAtom } from '../../atoms/tooltip';

interface IDefaultTooltip {
	children: JSX.Element | string;
	text: JSX.Element | string | any;
	isOpen?: boolean;
	disabled?: boolean;
	showOnClick?: boolean;
	canBeHovered?: boolean;
	zIndex?: number;
	display?: 'inline-block' | 'block' | 'inline' | 'flex';
	distance?: number;
	direction?: 'up' | 'down' | 'left' | 'right';
}

interface IWithCounter extends IDefaultTooltip {
	count: string;
	backgroundColor: string;
}

interface IWithTextLink extends IDefaultTooltip {
	textLink: string;
	setTooltipIsOpen: (tooltipIsOpen: boolean) => void;
}

const Badge = styled.div`
	display: inline;
	border-radius: 2px;
	text-align: center;
	margin-left: 8px;
	padding: 2px 8px;
	${(props: { backgroundColor: string }) => `background: ${props.backgroundColor}`}
`;

export const Default = ({
	children,
	text,
	showOnClick,
	isOpen,
	disabled,
	zIndex = 1000,
	display = 'inline-block',
	distance = 4,
	direction = 'down',
	canBeHovered = false,
}: IDefaultTooltip) => (
	<TooltipAtom
		content={
			<Text.FinePrint
				type={FinePrintEnum.CAPTION}
				colorVariant={ColorVariantEnum.WHITE}
				margin={MarginSizeEnum.NONE}
				weight={WeightEnum.SEMIBOLD}
				noLineHeight={true}
			>
				{text}
			</Text.FinePrint>
		}
		children={children}
		isOpen={disabled ? false : isOpen}
		canBeHovered={canBeHovered}
		showOnClick={showOnClick}
		zIndex={zIndex}
		display={display}
		distance={distance}
		direction={direction}
	/>
);

export const NoPermission = ({
	children,
	text,
	showOnClick,
	isOpen,
	disabled,
	zIndex = 1000,
	display = 'inline-block',
	distance = 4,
	direction = 'down',
	canBeHovered = false,
}: IDefaultTooltip) => (
	<TooltipAtom
		content={
			<React.Fragment>
				<Text.FinePrint
					type={FinePrintEnum.CAPTION}
					colorVariant={ColorVariantEnum.WHITE}
					margin={MarginSizeEnum.NONE}
					weight={WeightEnum.SEMIBOLD}
				>
					You don't have access to this feature
				</Text.FinePrint>
				<Text.FinePrint
					type={FinePrintEnum.CAPTION}
					colorVariant={ColorVariantEnum.WHITE}
					margin={MarginSizeEnum.NONE}
					weight={WeightEnum.SEMIBOLD}
					noLineHeight={true}
				>
					{/* for this tooltip, the text is fixed, so I use text prop to pass the role name */}
					{`with your current user role ${text ? ' ' + text.charAt(0).toUpperCase() + text.slice(1) : ''}.`}
				</Text.FinePrint>
			</React.Fragment>
		}
		children={children}
		isOpen={disabled ? false : isOpen}
		canBeHovered={canBeHovered}
		showOnClick={showOnClick}
		zIndex={zIndex}
		display={display}
		distance={distance}
		direction={direction}
	/>
);

export const WithCounter = ({ children, text, count, zIndex, display, direction, backgroundColor }: IWithCounter) => (
	<Default
		text={
			<React.Fragment>
				{text} <Badge backgroundColor={backgroundColor}>{count}</Badge>
			</React.Fragment>
		}
		children={children}
		zIndex={zIndex}
		display={display}
		direction={direction}
	/>
);
export const WithTextLink = ({
	children,
	text,
	textLink,
	isOpen,
	setTooltipIsOpen,
	zIndex,
	display,
	direction,
}: IWithTextLink) => {
	return (
		<Default
			text={
				<React.Fragment>
					<Text.FinePrint
						type={FinePrintEnum.CAPTION}
						colorVariant={ColorVariantEnum.WHITE}
						margin={MarginSizeEnum.SMALL}
						noLineHeight={true}
					>
						{text}
					</Text.FinePrint>
					<Text.FinePrint
						type={FinePrintEnum.CAPTION}
						colorVariant={ColorVariantEnum.WHITE}
						margin={MarginSizeEnum.NONE}
						weight={WeightEnum.SEMIBOLD}
						noLineHeight={true}
					>
						<Text.Link colorVariant={ColorVariantEnum.WHITE} onClick={() => setTooltipIsOpen(false)}>
							{textLink}
						</Text.Link>
					</Text.FinePrint>
				</React.Fragment>
			}
			children={children}
			isOpen={isOpen}
			canBeHovered={true}
			zIndex={zIndex}
			display={display}
			direction={direction}
		/>
	);
};

/**
 * The tooltip parent/triggerer should have the following props:
 * 1) data-tip
 * 2) data-for="same as tooltip id"
 */
export const Tooltip = {
	/**
	 * id {string} used to connect tooltip and tooltip triggerer
	 *
	 * text {string} used as text content of tooltip
	 *
	 * zIndex {number} used as the tooltip zIndex
	 *
	 * 	display {string} 'inline-block' | 'block' | 'inline' | 'flex' used as display css prop for the tooltip container / default is inline-block
	 *
	 *  distance {number} used to set the distance between the tooltip and the parent / defualt is 4px
	 *
	 *  direction {string} 'up' | 'down' | 'left' | 'right' used to set the tooltip direction / default is down
	 */
	Default,
	/**
	 * id {string} used to connect tooltip and tooltip triggerer
	 *
	 * text {string} used as text content of tooltip
	 *
	 * count {string} used as the text content of the counter badge on the tooltip
	 */
	WithCounter,
	/**
	 * id {string} used to connect tooltip and tooltip triggerer
	 *
	 * text {string} used as text content of tooltip
	 *
	 * textLink {string} used as the text content of the text link on the tooltip
	 *
	 * isOpen {boolean} withTextLink tooltip is showed on clicking and not on hover
	 * this is to know when to show/hide the tooltip
	 *
	 * setTooltipIsOpen {(tooltipIsOpen: boolean) => void} withTextLink tooltip is showed on clicking and not on hover,
	 * this is to update the state on the tooltip's father
	 */
	WithTextLink,
	/**
	 * this tooltip has a fixed message and should be only used on elements that are enabled/disabled based on a scope
	 *
	 * text {string} used to pass a user role to display it in the tooltip
	 */
	NoPermission,
};
